import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { GridItemHTMLElement, GridStackNode } from "gridstack";
import { AccountAppDTO } from "../apps/AccountApp";
import { SessionService } from "../session.service";
import { PermissionService } from "../permission.service";
import {
  AssistantThreadsWidget,
  AssistantDevicesUsersWidget,
  DeliveredContentWidget,
  DeliveriesByCategoryWidget,
  GenericWidget,
  InstallsWidget,
  MobileLocationAllowedWidget,
  OnPropertyWidget,
  RegionsSnapshotWidget,
  WeatherWidget,
  Widget,
  WidgetType,
  AssistantSharesRatingsFeedbackWidget,
} from "./widgets/widgets";
import { GridStackComponent } from "../shared/grid-stack/grid-stack.component";
import { WeatherWidgetComponent } from "./widgets/weather-widget/weather-widget.component";
import { GenericWidgetComponent } from "./widgets/generic-widget/generic-widget.component";
import { UserDashboardDTO } from "./Dashboard";
import { FormControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { DashboardService } from "./dashboard.service";
import { UiService } from "../ui/ui.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  FEATURE_AURA_KEY,
  FEATURE_EXPLORIST_KEY,
  FEATURE_TRIPIST_KEY,
} from "../features.constants";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  fullColumns = true;
  @ViewChild(GridStackComponent) gridStack!: GridStackComponent;
  @ViewChildren(WeatherWidgetComponent)
  weatherWidgetChildren!: QueryList<WeatherWidgetComponent>;
  @ViewChildren(GenericWidgetComponent)
  genericWidgetChildren!: QueryList<GenericWidgetComponent>;
  widgets: Widget[] = [];
  gridInitialized = false;
  currentDashboard!: UserDashboardDTO;
  filterForm!: FormGroup;
  addWidgetControl!: FormControl;
  availableWidgets!: { name: string; code: string; widgetType: WidgetType; assistantType?: "tripist" | "explorist" | "aura" }[];
  filteredWidgets!: { name: string; code: string; widgetType: WidgetType; assistantType?: "tripist" | "explorist" | "aura" }[];
  private onComponentDestroy = new Subject();
  private isInitialLoad = true;
  private readonly assistantTypeOrder: Array<"tripist" | "explorist" | "aura"> = [
    "tripist",
    "explorist",
    "aura",
  ];

  constructor(
    private SessionService: SessionService,
    private PermissionService: PermissionService,
    private cdRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private DashboardService: DashboardService,
    private uiService: UiService,
    private router: Router,
  ) {}

  get selectedFacilities() {
    return this.SessionService.selectedFacilities;
  }

  get currentUser() {
    return this.SessionService.currentUserValue;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  get apps() {
    return this.SessionService.apps;
  }

  get currentApp() {
    return this.SessionService.currentAppValue;
  }

  get currentAppObservable() {
    return this.SessionService.currentApp;
  }

  get permissionService() {
    return this.PermissionService;
  }

  get grid() {
    return this.gridStack?.grid;
  }

  defaultDashboard(): UserDashboardDTO {
    return {
      user_id: this.currentUser!.id,
      account_app_id: this.currentApp!.id,
      widgets: [
        {
          h: 2,
          w: 2,
          type: WidgetType.WEATHER,
          weatherOptions: {
            zipCode: this.selectedFacilities![0]?.zip ?? "85008",
            type: "F",
          },
        },
      ],
    };
  }

  getAvailableWidgets() {
    return this.availableWidgets.filter((widget) => {
      return this.PermissionService.isWidgetAvailable(widget.widgetType, this.currentAccount);
    });
  }

  ngOnInit(): void {

    this.filterForm = new FormGroup({
      startDate: new FormControl(this.route.snapshot.data.start),
      endDate: new FormControl(this.route.snapshot.data.end),
    });

    this.addWidgetControl = new FormControl();

    this.addWidgetControl.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change: string) => {
        const selectedWidget = this.availableWidgets.find((widget) => widget.code === change);
        if (!selectedWidget) {
          return;
        }

        if (selectedWidget.widgetType === WidgetType.WEATHER) {
          this.addWeatherWidget(1, 1);
        } else if (selectedWidget.widgetType === WidgetType.DELIVERIES_BY_CATEGORY) {
          this.addDeliveriesByCategoryWidget();
        } else if (selectedWidget.widgetType === WidgetType.INSTALLS) {
          this.addInstallsWidget();
        } else if (selectedWidget.widgetType === WidgetType.MOBILE_LOCATION_ALLOWED) {
          this.addMobileLocationAllowedWidget();
        } else if (selectedWidget.widgetType === WidgetType.REGIONS_SNAPSHOT) {
          this.addRegionsSnapshotWidget();
        } else if (selectedWidget.widgetType === WidgetType.ON_PROPERTY) {
          this.addOnPropertyWidget();
        } else if (selectedWidget.widgetType === WidgetType.DELIVERED_CONTENT) {
          this.addDeliveredContentWidget();
        } else if (selectedWidget.widgetType === WidgetType.ASSISTANT_THREADS) {
          this.addAssistantThreadsWidget(selectedWidget.assistantType);
        } else if (selectedWidget.widgetType === WidgetType.ASSISTANT_DEVICES_USERS) {
          this.addAssistantDevicesUsersWidget(selectedWidget.assistantType);
        } else if (selectedWidget.widgetType === WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK) {
          this.addAssistantSharesRatingsFeedbackWidget(selectedWidget.assistantType);
        }

        this.addWidgetControl.setValue(null, { emitEvent: false });

        this.checkAvailableWidgets();
      });

    this.availableWidgets = [
      {
        name: "Weather",
        code: WidgetType.WEATHER,
        widgetType: WidgetType.WEATHER,
      },
      {
        name: "Deliveries by Category",
        code: WidgetType.DELIVERIES_BY_CATEGORY,
        widgetType: WidgetType.DELIVERIES_BY_CATEGORY,
      },
      {
        name: "Installs",
        code: WidgetType.INSTALLS,
        widgetType: WidgetType.INSTALLS,
      },
      {
        name: "Mobile location permissions",
        code: WidgetType.MOBILE_LOCATION_ALLOWED,
        widgetType: WidgetType.MOBILE_LOCATION_ALLOWED,
      },
      {
        name: "Regions snapshot",
        code: WidgetType.REGIONS_SNAPSHOT,
        widgetType: WidgetType.REGIONS_SNAPSHOT,
      },
      {
        name: "On Property",
        code: WidgetType.ON_PROPERTY,
        widgetType: WidgetType.ON_PROPERTY,
      },
      {
        name: "Delivered Content",
        code: WidgetType.DELIVERED_CONTENT,
        widgetType: WidgetType.DELIVERED_CONTENT,
      },
    ];

    const enabledAssistants = this.getEnabledAssistantTypes();
    enabledAssistants.forEach((assistantType) => {
      const assistantName = this.getAssistantDisplayName(assistantType);
      this.availableWidgets.push(
        {
          name: `${assistantName} Threads`,
          code: `${WidgetType.ASSISTANT_THREADS}:${assistantType}`,
          widgetType: WidgetType.ASSISTANT_THREADS,
          assistantType,
        },
        {
          name: `${assistantName} Devices/Users`,
          code: `${WidgetType.ASSISTANT_DEVICES_USERS}:${assistantType}`,
          widgetType: WidgetType.ASSISTANT_DEVICES_USERS,
          assistantType,
        },
        {
          name: `${assistantName} Shares/Ratings/Feedback`,
          code: `${WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK}:${assistantType}`,
          widgetType: WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK,
          assistantType,
        },
      );
    });

    this.filteredWidgets = this.getAvailableWidgets();
    let appChange = 0;
    // On app change update dashboard
    this.currentAppObservable
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((currentApp: AccountAppDTO | null) => {
        if (currentApp) {
          if (appChange === 0) {
            this.widgets = [];
            this.isInitialLoad = true; // Reset for initial load

            this.uiService
              .waitForEvent(
                this.DashboardService.getDashboard(
                  this.currentAccount.accountID,
                  currentApp.id,
                ),
              )
              .subscribe((dashboard) => {
                //If an app is selected and no dashboard done, give user default dashboard
                if (this.currentApp && !dashboard) {
                  dashboard = this.defaultDashboard();
                }
                this.currentDashboard = dashboard!;

                // Create widgets preserving their original order from dashboard configuration
                // Use stable IDs based on widget position and type to prevent scrambling
                this.currentDashboard?.widgets.forEach((widget, dashboardIndex) => {
                  // Only create widgets that the current account has access to
                  if (!this.PermissionService.isWidgetAvailable(widget.type, this.currentAccount)) {
                    return; // Skip widgets without required features
                  }

                  // Normalize legacy assistant widget width from 2-column (6) to 3-column (4) layout.
                  if (
                    (widget.type === WidgetType.ASSISTANT_THREADS ||
                      widget.type === WidgetType.ASSISTANT_DEVICES_USERS ||
                      widget.type === WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK) &&
                    widget.w === 6
                  ) {
                    widget.w = 4;
                  }

                  // Create a stable ID based on the widget's position in the original dashboard config
                  // This ensures the same widget always gets the same ID regardless of account switches
                  const stableId = dashboardIndex;

                  if (widget.type === WidgetType.GENERIC) {
                    this.widgets.push(new GenericWidget(stableId, widget));
                  } else if (widget.type === WidgetType.WEATHER) {
                    this.widgets.push(new WeatherWidget(stableId, widget));
                  } else if (
                    widget.type === WidgetType.DELIVERIES_BY_CATEGORY
                  ) {
                    this.widgets.push(
                      new DeliveriesByCategoryWidget(stableId, widget),
                    );
                  } else if (widget.type === WidgetType.INSTALLS) {
                    this.widgets.push(new InstallsWidget(stableId, widget));
                  } else if (
                    widget.type === WidgetType.MOBILE_LOCATION_ALLOWED
                  ) {
                    this.widgets.push(
                      new MobileLocationAllowedWidget(stableId, widget),
                    );
                  } else if (widget.type === WidgetType.REGIONS_SNAPSHOT) {
                    this.widgets.push(new RegionsSnapshotWidget(stableId, widget));
                  } else if (widget.type === WidgetType.ON_PROPERTY) {
                    this.widgets.push(new OnPropertyWidget(stableId, widget));
                  } else if (widget.type === WidgetType.DELIVERED_CONTENT) {
                    this.widgets.push(
                      new DeliveredContentWidget(stableId, {
                        ...widget,
                        h: 2,
                      }),
                    );
                  } else if (widget.type === WidgetType.ASSISTANT_THREADS) {
                    this.widgets.push(new AssistantThreadsWidget(stableId, widget));
                  } else if (widget.type === WidgetType.ASSISTANT_DEVICES_USERS) {
                    this.widgets.push(new AssistantDevicesUsersWidget(stableId, widget));
                  } else if (widget.type === WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK) {
                    this.widgets.push(new AssistantSharesRatingsFeedbackWidget(stableId, widget));
                  }
                });

                // Sort widgets by y position first, then by x position to maintain proper grid order
                this.widgets.sort((a, b) => {
                  if (a.yPosition !== b.yPosition) {
                    return (a.yPosition || 0) - (b.yPosition || 0);
                  }
                  return (a.xPosition || 0) - (b.xPosition || 0);
                });

                this.checkAvailableWidgets();
              });
            appChange++;
            
            // Set initial load to false after the first load is complete
            this.isInitialLoad = false;
          } else {
            //After initial app is changed, dirty reload the dashboard component
            this.isInitialLoad = true; // Reset for account switch

            const defualtShouldReuseRoute =
              this.router.routeReuseStrategy.shouldReuseRoute;
            const defaultOnSameUrlNavigation = this.router.onSameUrlNavigation;

            const currentUrl = this.router.url;

            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
            this.router.onSameUrlNavigation = "reload";
            this.router.navigate([currentUrl]).finally(() => {
              //Reset back to default after dirty reload

              this.router.routeReuseStrategy.shouldReuseRoute =
                defualtShouldReuseRoute;
              this.router.onSameUrlNavigation = defaultOnSameUrlNavigation;
            });
          }
        }
      });
  }

  ngAfterViewInit() {

    if(!this.currentApp){
      return;
    }

    //Manually check for changes due to issue with grid changing value after change detection
    this.cdRef.detectChanges();

    this.setColumns();

    //On grid item change update the widget to persist h, w, x, y values
    //TODO: Test change handler for other grid changes that may not return a GridStackNode[]
    this.grid.on(
      "change",
      (
        _event: Event,
        items:
          | GridStackNode
          | GridItemHTMLElement
          | GridStackNode[]
          | undefined,
      ) => {
        (items as GridStackNode[])?.forEach((item) => {
          const itemCode = (item.el?.attributes as any).code?.value;
          const updatedWidget = this.widgets.find(
            (widget) => widget.code === itemCode,
          );
          updatedWidget?.updateWidgetValues(item);
        });

        // Only save if it's not the initial load
        if (!this.isInitialLoad) {
          this.saveDashboard();
        }
      },
    );

    //Once drag is done turn grid moveable to false
    this.grid.on(
      "dragstop",
      (
        event: Event,
        el: GridStackNode | GridItemHTMLElement | GridStackNode[] | undefined,
      ) => {
        this.grid.setStatic(true);
        //TODO: Look into this: Drag only working when enableMove is called twice
        this.grid.enableMove(false);
        this.grid.enableMove(false);
      },
    );

    //When widget is added, add x and y generated by gridstack to the widgetDTO then save the dashboard
    this.grid.on(
      "added",
      (
        event: Event,
        gridStackNodes:
          | GridStackNode
          | GridItemHTMLElement
          | GridStackNode[]
          | undefined,
      ) => {
        const gsNodeX = (gridStackNodes as GridStackNode[])[0].x;
        const gsNodeY = (gridStackNodes as GridStackNode[])[0].y;
        const addedWidget = this.widgets.find((widget) => {
          return (
            `grid-item-${widget.code}` ===
            (gridStackNodes as GridStackNode[])[0].el?.id
          );
        });
        if (addedWidget) {
          addedWidget.widgetDTO.x = gsNodeX;
          addedWidget.widgetDTO.y = gsNodeY;
        }
        this.cdRef.detectChanges();

        this.saveDashboard();
      },
    );
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();

    if(!this.currentApp){
      return;
    }

    this.grid.destroy();
  }

  onResize(event: any) {
    this.setColumns();
  }

  setColumns() {
    const clientWidth = this.grid.el.clientWidth;
    if (this.fullColumns && clientWidth < 1200) {
      this.fullColumns = false;
      this.grid.column(6, "none");
    } else if (!this.fullColumns && clientWidth >= 1200) {
      this.fullColumns = true;
      this.grid.column(12, "none");
    }
  }

  //Reload grid to move neighbors if needed and save dashboard
  onResizeWidget() {
    this.reloadGrid();
    this.saveDashboard();
  }

  reloadGrid() {
    this.cdRef.detectChanges();
    this.grid.load(this.grid.getGridItems());
  }

  addWeatherWidget(width: number, height: number) {
    // Generate stable ID based on current dashboard config length
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new WeatherWidget(nextStableId, {
        h: height * 2,
        w: width * 2,
        type: WidgetType.WEATHER,
        weatherOptions: {
          zipCode: this.selectedFacilities![0]?.zip ?? "85008",
          type: "F",
        },
      }),
    );
  }

  addDeliveriesByCategoryWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new DeliveriesByCategoryWidget(nextStableId, {
        h: 3,
        w: 3,
        type: WidgetType.DELIVERIES_BY_CATEGORY,
      }),
    );
  }

  addInstallsWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new InstallsWidget(nextStableId, {
        h: 2,
        w: 3,
        type: WidgetType.INSTALLS,
      }),
    );
  }

  addMobileLocationAllowedWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new MobileLocationAllowedWidget(nextStableId, {
        h: 2,
        w: 3,
        type: WidgetType.MOBILE_LOCATION_ALLOWED,
      }),
    );
  }

  addRegionsSnapshotWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new RegionsSnapshotWidget(nextStableId, {
        h: 2,
        w: 3,
        type: WidgetType.REGIONS_SNAPSHOT,
      }),
    );
  }

  addOnPropertyWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new OnPropertyWidget(nextStableId, {
        h: 4,
        w: 3,
        type: WidgetType.ON_PROPERTY,
      }),
    );
  }

  addDeliveredContentWidget() {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    this.widgets.push(
      new DeliveredContentWidget(nextStableId, {
        h: 2,
        w: 6,
        type: WidgetType.DELIVERED_CONTENT,
      }),
    );
  }

  addAssistantThreadsWidget(assistantType?: "tripist" | "explorist" | "aura") {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    const resolvedAssistantType = assistantType || this.getNextAssistantTypeForWidget(WidgetType.ASSISTANT_THREADS);
    this.widgets.push(
      new AssistantThreadsWidget(nextStableId, {
        h: 2,
        w: 4,
        type: WidgetType.ASSISTANT_THREADS,
        assistantType: resolvedAssistantType,
      }),
    );
  }

  addAssistantDevicesUsersWidget(assistantType?: "tripist" | "explorist" | "aura") {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    const resolvedAssistantType = assistantType || this.getNextAssistantTypeForWidget(WidgetType.ASSISTANT_DEVICES_USERS);
    this.widgets.push(
      new AssistantDevicesUsersWidget(nextStableId, {
        h: 2,
        w: 4,
        type: WidgetType.ASSISTANT_DEVICES_USERS,
        assistantType: resolvedAssistantType,
      }),
    );
  }

  addAssistantSharesRatingsFeedbackWidget(assistantType?: "tripist" | "explorist" | "aura") {
    const nextStableId = this.currentDashboard?.widgets?.length || 0;
    const resolvedAssistantType = assistantType || this.getNextAssistantTypeForWidget(WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK);
    this.widgets.push(
      new AssistantSharesRatingsFeedbackWidget(nextStableId, {
        h: 2,
        w: 4,
        type: WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK,
        assistantType: resolvedAssistantType,
      }),
    );
  }

  private getEnabledAssistantTypes(): Array<"tripist" | "explorist" | "aura"> {
    const enabled: Array<"tripist" | "explorist" | "aura"> = [];
    if (this.PermissionService.hasFeature(FEATURE_TRIPIST_KEY, this.currentAccount)) {
      enabled.push("tripist");
    }
    if (this.PermissionService.hasFeature(FEATURE_EXPLORIST_KEY, this.currentAccount)) {
      enabled.push("explorist");
    }
    if (this.PermissionService.hasFeature(FEATURE_AURA_KEY, this.currentAccount)) {
      enabled.push("aura");
    }
    return enabled;
  }

  private getAssistantDisplayName(type: "tripist" | "explorist" | "aura"): string {
    if (type === "tripist") return "Tripist";
    if (type === "explorist") return "Explorist";
    return "Aura";
  }

  private getNextAssistantTypeForWidget(
    widgetType: WidgetType,
  ): "tripist" | "explorist" | "aura" | undefined {
    const enabledTypes = this.getEnabledAssistantTypes();
    if (enabledTypes.length === 0) {
      return undefined;
    }

    const usedTypes = new Set(
      this.widgets
        .filter((widget) => widget.type === widgetType)
        .map((widget) => widget.widgetDTO.assistantType)
        .filter((assistantType): assistantType is "tripist" | "explorist" | "aura" =>
          assistantType === "tripist" || assistantType === "explorist" || assistantType === "aura"
        ),
    );

    const orderedEnabledTypes = this.assistantTypeOrder.filter((type) => enabledTypes.includes(type));
    return orderedEnabledTypes.find((type) => !usedTypes.has(type)) || orderedEnabledTypes[0];
  }

  toGridStackWidgetOptions(widget: any) {
    return {
      h: widget.h,
      w: widget.w,
      y: widget.y,
      x: widget.x,
    };
  }

  saveDashboard() {
    const widgetsDTO = this.widgets.map((widget) => widget.widgetDTO);

    const dashboardDTO = {
      ...this.currentDashboard,
      widgets: widgetsDTO,
    };

    if (this.currentDashboard.id) {
      this.uiService
        .waitForEvent(
          this.DashboardService.updateDashboard(
            this.currentAccount.accountID,
            this.currentApp!.id,
            dashboardDTO,
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (res) => {},
          (err) => {},
        );
    } else {
      this.uiService
        .waitForEvent(
          this.DashboardService.createDashboard(
            this.currentAccount.accountID,
            this.currentApp!.id,
            dashboardDTO,
          ),
        )
        .pipe(takeUntil(this.onComponentDestroy))
        .subscribe(
          (res) => {
            this.currentDashboard = res;
          },
          (err) => {},
        );
    }
  }

  enableMoveWidgets() {
    this.grid.setStatic(false);
    //TODO: Look into this: Drag only working when enableMove is called twice
    this.grid.enableMove(true);
    this.grid.enableMove(true);
  }

  disableMoveWidgets() {
    //Fallback incase dragstop event is not triggered in dashboard component
    this.grid.setStatic(true);
    this.grid.enableMove(false);
    this.grid.enableMove(false);
  }

  onGridInit() {
    this.gridInitialized = true;
  }

  deleteWidget(widget: Widget) {
    const widgetIndex = this.widgets.indexOf(widget);
    this.widgets.splice(widgetIndex, 1);
    this.checkAvailableWidgets();
    this.reloadGrid();

    this.saveDashboard();
  }

  checkAvailableWidgets() {
    this.filteredWidgets = this.getAvailableWidgets().filter((availableWidget) => {
      if (
        availableWidget.widgetType === WidgetType.ASSISTANT_THREADS ||
        availableWidget.widgetType === WidgetType.ASSISTANT_DEVICES_USERS ||
        availableWidget.widgetType === WidgetType.ASSISTANT_SHARES_RATINGS_FEEDBACK
      ) {
        const assistantType = availableWidget.assistantType;
        if (!assistantType) {
          return true;
        }
        const exists = this.widgets.some((widget) => {
          const existingAssistantType =
            widget.widgetDTO.assistantType ||
            this.getEnabledAssistantTypes()[0] ||
            "tripist";
          return widget.type === availableWidget.widgetType && existingAssistantType === assistantType;
        });
        return !exists;
      }

      const widget = this.widgets.find((widget) => widget.type === availableWidget.widgetType);
      return !widget || widget.multiple;
    });
  }
}
